// client/src/features/utilities/enums.ts — the three wire enums SCR-UTIL-001…003 render ·
// MAINT-008.
//
// All three are GENERATED unions (`@/client`), declared here as `as const satisfies readonly
// T[]` — `features/repairs/enums.ts`'s shape, for the reason it gives: the array is a literal
// tuple the filters and the pickers iterate, and a member the contract adds or drops is a
// compile error in the PR that regenerated the client rather than a filter that quietly stops
// offering a kind of supply.
//
// `UtilityCostBearer` is NOT one of them and has no array here: it is a two-member answer the
// server derives from `whoPays` and never a thing a user picks (D-973). It is rendered through
// `enums.UtilityCostBearer.*` where it arrives, and nothing iterates it.

import type {
  PaymentMethod,
  UtilityBillChannel,
  UtilityBillStatus,
  UtilityResponsibility,
  UtilityType,
} from '@/client';
import { PAYMENT_METHODS } from '@/features/payments/enums';

/**
 * FOUNDATION_BRIEF §3's `UtilityType`, in the order the enum declares it — electricity first
 * because it is the meter a landlord reads most, „altele" last because a fallback belongs at
 * the end of a picker rather than in the middle of one.
 */
export const UTILITY_TYPES = [
  'ELECTRICITY',
  'GAS',
  'WATER_SEWER',
  'HEATING',
  'SANITATION',
  'INTERNET_TV',
  'OTHER',
] as const satisfies readonly UtilityType[];

/**
 * Who pays the provider. The order is the enum's, and it is also the order that reads: the two
 * members that put the bill on the tenant first, then the two that leave it with the owner.
 */
export const UTILITY_RESPONSIBILITIES = [
  'TENANT',
  'OWNER',
  'SHARED',
  'INCLUDED_IN_RENT',
] as const satisfies readonly UtilityResponsibility[];

/**
 * The units 03 §11.2 names for `measurement_unit` — kWh, mc, Gcal, buc.
 *
 * A closed picker with an „Altă unitate…" escape hatch (D-2310): the column is `varchar(10)`
 * free text and `UtilityServiceInputValidator` checks only its width, so a provider that bills
 * in something else must still be recordable. The four are the suggestions, not the rule (D-1126).
 */
export const MEASUREMENT_UNITS = ['kWh', 'mc', 'Gcal', 'buc'] as const;

/**
 * The unit a service of each type is normally billed in. Choosing a type in the form proposes
 * this unit until the user picks one by hand (D-2311); it is a suggestion, never a constraint.
 */
export const DEFAULT_UNIT_BY_TYPE = {
  ELECTRICITY: 'kWh',
  GAS: 'mc',
  WATER_SEWER: 'mc',
  HEATING: 'Gcal',
  SANITATION: 'buc',
  INTERNET_TV: 'buc',
  OTHER: 'buc',
} as const satisfies Record<(typeof UTILITY_TYPES)[number], (typeof MEASUREMENT_UNITS)[number]>;

// ── the utility BILL's enums (D-2203) ──────────────────────────────────────────────────────
//
// Two more generated unions, declared the way the three above are and for the same reason: the
// „Plăți" tab filters on a status and prints a channel, and a member the contract
// adds or drops must be a compile error in the PR that regenerated the client rather than a
// column that quietly stops saying where a row came from.

/**
 * The three rungs of a declared bill. `PENDING` is the one waiting on the owner, `CONFIRMED`
 * the one they believed, `REJECTED` the one they refused — and a `REJECTED` row is not live, so
 * the month can be declared again (D-2183).
 */
export const UTILITY_BILL_STATUSES = [
  'PENDING',
  'CONFIRMED',
  'REJECTED',
] as const satisfies readonly UtilityBillStatus[];

/**
 * Who wrote the row: the renter from the portal, or the owner from SCR-UTIL-003. Not a status
 * and never coloured as one — it is provenance, the same kind of fact `PaymentSource` carries
 * (D-706).
 */
export const UTILITY_BILL_CHANNELS = [
  'TENANT_PORTAL',
  'OWNER',
] as const satisfies readonly UtilityBillChannel[];

/**
 * The methods the owner's „Înregistrează o plată" offers: `PAYMENT_METHODS` minus
 * `COMPENSATION`, which is what `RecordUtilityBillCommandValidator` refuses — „o factură de
 * utilități nu se plătește prin compensare". Derived from the payments list rather than
 * re-spelled, so a method the contract adds reaches this picker with it.
 *
 * The portal's own four (`DeclareUtilityBillCommandValidator.PortalMethods`) are a narrower set
 * still; that list is the tenant lane's and is not this module's to hold.
 */
export const BILL_METHODS: readonly PaymentMethod[] = PAYMENT_METHODS.filter(
  (method) => method !== 'COMPENSATION',
);
